import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from "../Common/Modal";
import { HiPlay, HiPause, HiShare, HiDownload } from "react-icons/hi";
import api from "../../services/api";

interface AudioPlayerModalProps {
  callLog: {
    id: number;
    contact_name: string;
    duration: number;
    call_recording_url: string | null;
    cost: number;
  };
  onClose: () => void;
}

export const AudioPlayerModal: React.FC<AudioPlayerModalProps> = ({ callLog, onClose }) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [shareMessage, setShareMessage] = useState<string>("");

  const audioRef = useRef<HTMLAudioElement>(null);

  const fetchAudioUrl = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/campaigns/call-logs/${callLog.id}/audio-url`);
      setAudioUrl(response.data.signed_url);
    } catch (error) {
      console.error("Error fetching audio URL:", error);
      setError("Failed to load audio");
    } finally {
      setLoading(false);
    }
  }, [callLog.id]);

  useEffect(() => { 
    fetchAudioUrl();
    
    const audio = audioRef.current;
    return () => {
      // Cleanup audio on unmount
      if (audio) {
        audio.pause();
        audio.src = "";
      }
    };
  }, [callLog.id, fetchAudioUrl]);

  

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleShare = async () => {
    try {
      // Generate universal URL (remove admin prefix for cross-user compatibility)
      let shareUrl = window.location.href;
      if (shareUrl.includes('/admin/campaigns/')) {
        shareUrl = shareUrl.replace('/admin/campaigns/', '/campaigns/');
      }
      
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage("Link copied to clipboard!");
      setTimeout(() => setShareMessage(""), 3000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      setShareMessage("Failed to copy link");
      setTimeout(() => setShareMessage(""), 3000);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/campaigns/call-logs/${callLog.id}/download-url`);
      const { download_url, filename } = response.data;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename || `recording_${callLog.id}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading recording:', error);
      setShareMessage("Failed to download recording");
      setTimeout(() => setShareMessage(""), 3000);
    }
  };

  return (
    <Modal isOpen={true} title={`Call Recording - ${callLog.contact_name}`} onClose={onClose}>
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">Loading audio...</div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">{error}</div>
        ) : (
          <div className="space-y-4">
            {/* Audio element */}
            <audio
              ref={audioRef}
              src={audioUrl || undefined}
              onTimeUpdate={handleTimeUpdate}
              onEnded={() => setPlaying(false)}
              // autoPlay
            />

            {/* Player controls */}
            <div className="bg-[var(--muted)] rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={togglePlay}
                  className="w-12 h-12 flex items-center justify-center bg-primary text-primary-foreground rounded-full hover:bg-primary/90"
                >
                  {playing ? (
                    <HiPause className="h-6 w-6" />
                  ) : (
                    <HiPlay className="h-6 w-6 ml-0.5" />
                  )}
                </button>

                <div className="flex-1 mx-4">
                  <input
                    type="range"
                    min="0"
                    max={callLog.duration}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-2 bg-[var(--border)] rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-sm text-[var(--foreground-muted)] mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(callLog.duration)}</span>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="mt-4 pt-4 border-t border-[var(--border)]">
                <div className="flex gap-2">
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 px-4 py-2 text-[var(--foreground)] hover:text-primary border border-[var(--border)] rounded-lg hover:bg-[var(--background)] transition-colors flex-1 justify-center"
                    title="Copy link"
                  >
                    <HiShare className="h-4 w-4" />
                    Copy Link
                  </button>
                  
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-4 py-2 text-[var(--foreground)] hover:text-primary border border-[var(--border)] rounded-lg hover:bg-[var(--background)] transition-colors flex-1 justify-center"
                    title="Download recording"
                  >
                    <HiDownload className="h-4 w-4" />
                    Download
                  </button>
                </div>

                {/* Message */}
                {shareMessage && (
                  <div className="mt-2 text-center text-sm text-green-600">
                    {shareMessage}
                  </div>
                )}
              </div>

            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}; 